import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';
import { adminTicketsAPI, adminLookupAPI } from '../../services/api';

/**
 * Edit common fields on an existing ticket: summary, board (move ticket),
 * status, type/sub-type/item, priority, source, agreement.
 *
 * Pre-fills from the current ticket; only sends fields the user actually
 * changed. Moving boards also resets the status/type cascades, so the user
 * must pick a new status that's valid on the new board.
 */
export default function AdminEditTicketModal({ ticket, onClose, onSuccess }) {
  const [summary, setSummary] = useState(ticket?.summary || '');
  const [boardId, setBoardId] = useState(ticket?.board?.id ? String(ticket.board.id) : '');
  const [statusId, setStatusId] = useState(ticket?.status?.id ? String(ticket.status.id) : '');
  const [priorityId, setPriorityId] = useState(
    ticket?.priority?.id ? String(ticket.priority.id) : ''
  );
  const [typeId, setTypeId] = useState(ticket?.type?.id ? String(ticket.type.id) : '');
  const [subTypeId, setSubTypeId] = useState(ticket?.subType?.id ? String(ticket.subType.id) : '');
  const [itemId, setItemId] = useState(ticket?.item?.id ? String(ticket.item.id) : '');
  const [sourceId, setSourceId] = useState(ticket?.source?.id ? String(ticket.source.id) : '');
  const [agreementId, setAgreementId] = useState(
    ticket?.agreement?.id ? String(ticket.agreement.id) : ''
  );
  const [error, setError] = useState(null);
  const [originalBoardId] = useState(boardId);

  const { data: boardsData } = useQuery({
    queryKey: ['admin-lookup-boards'],
    queryFn: () => adminLookupAPI.boards(),
  });
  const boards = boardsData?.data?.items || [];

  const { data: prioritiesData } = useQuery({
    queryKey: ['admin-lookup-priorities'],
    queryFn: () => adminLookupAPI.priorities(),
  });
  const priorities = prioritiesData?.data?.items || [];

  const { data: sourcesData } = useQuery({
    queryKey: ['admin-lookup-sources'],
    queryFn: () => adminLookupAPI.sources(),
  });
  const sources = sourcesData?.data?.items || [];

  const { data: statusesData } = useQuery({
    queryKey: ['admin-lookup-statuses', boardId],
    queryFn: () => adminLookupAPI.boardStatuses(boardId),
    enabled: !!boardId,
  });
  const statuses = statusesData?.data?.items || [];

  const { data: typesData } = useQuery({
    queryKey: ['admin-lookup-types', boardId],
    queryFn: () => adminLookupAPI.boardTypes(boardId),
    enabled: !!boardId,
  });
  const types = typesData?.data?.items || [];

  const { data: subTypesData } = useQuery({
    queryKey: ['admin-lookup-subtypes', boardId, typeId],
    queryFn: () => adminLookupAPI.boardSubTypes(boardId, typeId),
    enabled: !!boardId && !!typeId,
  });
  const subTypes = subTypesData?.data?.items || [];

  const { data: itemsData } = useQuery({
    queryKey: ['admin-lookup-items', boardId],
    queryFn: () => adminLookupAPI.boardItems(boardId),
    enabled: !!boardId,
  });
  const items = itemsData?.data?.items || [];

  const { data: agreementsData } = useQuery({
    queryKey: ['admin-lookup-agreements', ticket?.company?.id],
    queryFn: () => adminLookupAPI.companyAgreements(ticket.company.id),
    enabled: !!ticket?.company?.id,
  });
  const agreements = agreementsData?.data?.items || [];

  // When board changes, clear cascaded fields to force re-pick
  useEffect(() => {
    if (boardId !== originalBoardId) {
      setStatusId('');
      setTypeId('');
      setSubTypeId('');
      setItemId('');
    }
  }, [boardId, originalBoardId]);

  const updateMutation = useMutation({
    mutationFn: (data) => adminTicketsAPI.updateTicket(ticket.id, data),
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onError: (err) =>
      setError(err.response?.data?.error || err.response?.data?.details || 'Failed to update ticket'),
  });

  const submit = () => {
    const payload = {};
    if (summary !== (ticket?.summary || '')) payload.summary = summary;
    if (boardId !== (ticket?.board?.id ? String(ticket.board.id) : ''))
      payload.boardId = boardId ? parseInt(boardId) : null;
    if (statusId !== (ticket?.status?.id ? String(ticket.status.id) : ''))
      payload.statusId = statusId ? parseInt(statusId) : null;
    if (priorityId !== (ticket?.priority?.id ? String(ticket.priority.id) : ''))
      payload.priorityId = priorityId ? parseInt(priorityId) : null;
    if (typeId !== (ticket?.type?.id ? String(ticket.type.id) : ''))
      payload.typeId = typeId ? parseInt(typeId) : null;
    if (subTypeId !== (ticket?.subType?.id ? String(ticket.subType.id) : ''))
      payload.subTypeId = subTypeId ? parseInt(subTypeId) : null;
    if (itemId !== (ticket?.item?.id ? String(ticket.item.id) : ''))
      payload.itemId = itemId ? parseInt(itemId) : null;
    if (sourceId !== (ticket?.source?.id ? String(ticket.source.id) : ''))
      payload.sourceId = sourceId ? parseInt(sourceId) : null;
    if (agreementId !== (ticket?.agreement?.id ? String(ticket.agreement.id) : ''))
      payload.agreementId = agreementId ? parseInt(agreementId) : null;
    if (Object.keys(payload).length === 0) {
      setError('Nothing to save — no fields changed.');
      return;
    }
    updateMutation.mutate(payload);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-auto"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-800 z-10">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Edit Ticket</h2>
          <button
            onClick={onClose}
            className="p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <Field label="Summary">
            <input
              type="text"
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, 100))}
              maxLength={100}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm"
            />
            <div className="text-right text-xs text-neutral-400 mt-1">{summary.length} / 100</div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Board">
              <Select
                value={boardId}
                onChange={setBoardId}
                options={boards.map((b) => ({ value: String(b.id), label: b.name }))}
              />
              {boardId !== originalBoardId && (
                <div className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                  Changing boards resets status / type / item — pick fresh values below.
                </div>
              )}
            </Field>
            <Field label="Status">
              <Select
                value={statusId}
                onChange={setStatusId}
                options={statuses.map((s) => ({ value: String(s.id), label: s.name }))}
                disabled={!boardId}
              />
            </Field>
            <Field label="Priority">
              <Select
                value={priorityId}
                onChange={setPriorityId}
                options={priorities.map((p) => ({ value: String(p.id), label: p.name }))}
                allowEmpty
              />
            </Field>
            <Field label="Source">
              <Select
                value={sourceId}
                onChange={setSourceId}
                options={sources.map((s) => ({ value: String(s.id), label: s.name }))}
                allowEmpty
              />
            </Field>
            <Field label="Type">
              <Select
                value={typeId}
                onChange={(v) => {
                  setTypeId(v);
                  setSubTypeId('');
                }}
                options={types.map((t) => ({ value: String(t.id), label: t.name }))}
                disabled={!boardId}
                allowEmpty
              />
            </Field>
            <Field label="Sub-Type">
              <Select
                value={subTypeId}
                onChange={setSubTypeId}
                options={subTypes.map((s) => ({ value: String(s.id), label: s.name }))}
                disabled={!typeId}
                allowEmpty
              />
            </Field>
            <Field label="Item">
              <Select
                value={itemId}
                onChange={setItemId}
                options={items.map((i) => ({ value: String(i.id), label: i.name }))}
                disabled={!boardId}
                allowEmpty
              />
            </Field>
            <Field label="Agreement">
              <Select
                value={agreementId}
                onChange={setAgreementId}
                options={[
                  { value: '', label: 'None' },
                  ...agreements.map((a) => ({ value: String(a.id), label: a.name })),
                ]}
                allowEmpty
              />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-neutral-200 dark:border-neutral-700 sticky bottom-0 bg-white dark:bg-neutral-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
          >
            Cancel
          </button>
          <button
            disabled={updateMutation.isPending}
            onClick={submit}
            className="px-4 py-2 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, disabled, allowEmpty }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-sm disabled:opacity-50"
    >
      {allowEmpty && <option value="">— None —</option>}
      {!allowEmpty && <option value="">Pick…</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
